import { mapState } from 'vuex'
import { isImpersonating } from 'src/utils'
import * as AnswerTypes from '../../constants/answer-types'
import * as User from '../../constants/user'
import { get, isEmpty } from 'lodash'
import * as storage from 'src/plugins/helpers/storage'
import { UNRESTRICTED_ROLES } from 'src/constants/roles'
import { agentAvailableCampaignsCallback } from 'src/plugins/helpers/campaigns'

export default {
  computed: {
    ...mapState(['users', 'usersIsLoading']),

    ...mapState('cache', ['currentCompany']),

    isInboxViewsEnabledCompany () {
      const companyIds = [1261, 1568] // JobNimbus, Cardone Ventures
      return companyIds.includes(this.profile.company_id)
    },

    isModGen () {
      return this.currentCompany?.reseller_id === 2132
    },

    isImpersonate () {
      return isImpersonating()
    },

    isDemoCompany () {
      return this.isCompanyPartOfAlowareDemoCompanies(this.currentCompany?.id)
    },

    isProduction () {
      return storage.local.getItem('env') === 'production'
    },

    hasCompanyTeamInboxLineManagementEnhancements () {
      return this.currentCompany?.team_inbox_line_management_enhancements === true
    },

    /**
     * Whether the line selectors should only offer the lines the current user reaches
     * through their Inbox membership.
     *
     * Only the unrestricted roles keep the account-wide view. Every other role —
     * Supervisors included — is scoped by Inbox membership, mirroring the
     * contacts/communications visibility rules enforced on the backend.
     *
     * Expressed as an exclusion so additive roles (e.g. Company Reporter Access) resolve
     * correctly in any combination. A profile that hasn't loaded yet therefore reads as
     * scoped: an empty selector for a moment is the safe way to be wrong here, where
     * offering the whole account is the bug this rule exists to prevent.
     *
     * Requires aclMixin for hasRole().
     *
     * @see UNRESTRICTED_ROLES
     * @returns {Boolean}
     */
    shouldLimitLinesVisibilityByInbox () {
      return !this.hasRole(UNRESTRICTED_ROLES) &&
        this.hasCompanyTeamInboxLineManagementEnhancements
    },

    isVoicemailDropDisabledAtAccountLevel () {
      return this.currentCompany?.enable_voicemail_drop === false
    },

    shouldSeeExperimentalAiFeatures () {
      return this.isDemoCompany || this.currentCompany?.plan?.ai_plan === true
    },

    shouldSeeExperimentalXproAiFeatures () {
      return (this.shouldSeeExperimentalAiFeatures && this.currentCompany?.plan?.use_case === 'xPro') || this.isDemoCompany
    }
  },

  methods: {
    /**
     * Narrow a list of lines down to the ones the current user may use.
     *
     * Every line selector must route its options through this so the rule stays in
     * one place. Pass force when the context scopes by Inbox membership regardless
     * of role, such as the All Inboxes view.
     *
     * @param {Array} campaigns
     * @param {Object} [options]
     * @param {Boolean} [options.force]
     * @returns {Array}
     */
    limitLinesToInboxAccess (campaigns, { force = false } = {}) {
      if (!force && !this.shouldLimitLinesVisibilityByInbox) {
        return campaigns
      }

      return campaigns.filter(campaign => agentAvailableCampaignsCallback(campaign, this.profile?.id))
    },

    /**
     * Whether the current user may use a single line. The counterpart of
     * limitLinesToInboxAccess() for the places that check one line rather than
     * filter a list.
     *
     * @param {Object} campaign
     * @returns {Boolean}
     */
    canAccessLine (campaign) {
      if (!this.shouldLimitLinesVisibilityByInbox) {
        return true
      }

      // The helper's || chain resolves to the last missing flag rather than false.
      return Boolean(agentAvailableCampaignsCallback(campaign, this.profile?.id))
    },

    getUser (id) {
      const users = get(this, 'users', null)

      if (!id || !users || users.length === 0) {
        return {
          id: id,
          name: ''
        }
      }

      const found = users.find(user => user.id === id)

      if (found) {
        return found
      }

      return {
        id: id,
        name: ''
      }
    },

    isAiAgentUser (user) {
      return user && user.type === User.TYPE_AI_AGENT
    },

    getUserName (user) {
      if (!user) {
        return
      }

      // sanity check
      if (!user.id) {
        return
      }

      // an answer type label always carries a name; only the fallback below, for a record with
      // neither a name nor a phone number, is allowed to read as a deleted user
      const name = user.name || 'No Name'

      switch (user.answer_by) {
        case AnswerTypes.BY_BROWSER:
          return name + ' - Browser / Apps'
        case AnswerTypes.BY_PHONE_NUMBER:
          if (user.phone_number) {
            return name + ' - Phone Number (' + user.phone_number + ')'
          }

          return name
        case AnswerTypes.BY_NONE:
          return name + ' - Will Not Answer'
      }

      if (user.name) {
        return user.phone_number ? user.name + ' (' + user.phone_number + ')' : user.name
      }

      if (user.phone_number) {
        return 'No Name (' + user.phone_number + ')'
      }

      return 'Deleted User'
    },

    isCompanyPartOfAlowareDemoCompanies (companyId) {
      return storage.local.getItem('aloware_demo_companies') && storage.local.getItem('aloware_demo_companies').split(',').includes(String(companyId))
    },

    // Filters users by excluding those with read-only access and a single/undefined role,
    // or those with `answer_by` set to `BY_NONE`.
    filterUsers (users) {
      if (isEmpty(users)) return []

      return users.filter((user) => {
        const hasSingleOrUndefinedRole = typeof user.role_names === 'undefined' || user.role_names.length === 1

        return !(hasSingleOrUndefinedRole && user.read_only_access) && user.answer_by !== AnswerTypes.BY_NONE
      })
    }
  }
}
